import React, { useEffect, useMemo, useState } from 'react';
import { Clock, ChevronDown, ChevronRight, Loader2, AlertTriangle } from 'lucide-react';
import { groupSkipReasons } from '../utils/skipReasons';
import azureDevOpsService from '../services/azureDevOpsService';

/**
 * Synthèse des tests ignorés, regroupés par motif.
 *
 * Le composant se charge lui-même à partir des builds qu'on lui donne : les six
 * dashboards d'équipe étant des copies l'un de l'autre, tout ce qui reste dans la page
 * doit être répliqué six fois — ici, seul le calcul des buildIds l'est.
 *
 * Le compteur d'ignorés vient du résumé de run, les motifs des résultats détaillés :
 * les deux peuvent diverger (résultat non téléchargeable, run tronqué). Plutôt que
 * d'afficher un total partiel comme s'il était complet, l'écart est rendu explicite.
 *
 * @param {Array<{buildId: string|number, label?: string}>} builds - builds à interroger
 * @param {number} expectedTotal - nombre d'ignorés annoncé par les compteurs de la page
 * @param {string} title         - titre du bloc
 */
export default function SkippedTestsPanel({
  builds = [],
  expectedTotal = 0,
  title = 'Tests ignorés',
  defaultOpen = false,
}) {
  const [open, setOpen] = useState(defaultOpen);
  const [expandedKey, setExpandedKey] = useState(null);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // `builds` est recréé à chaque rendu par les pages appelantes : on dépend de sa
  // sérialisation, sinon l'effet reboucle indéfiniment.
  const buildsKey = useMemo(
    () => builds.filter(b => b?.buildId).map(b => `${b.buildId}:${b.label || ''}`).sort().join('|'),
    [builds]
  );

  useEffect(() => {
    // Rien d'ignoré annoncé : inutile d'aller chercher des motifs qui n'existent pas.
    if (!buildsKey || !expectedTotal) {
      setResults([]);
      setError(null);
      return;
    }

    let mounted = true;
    const fetchSkipped = async () => {
      setLoading(true);
      setError(null);
      try {
        const targets = buildsKey.split('|').map(entry => {
          // Un buildId ne contient jamais de ':' — le premier délimite donc toujours le libellé,
          // même quand celui-ci en contient.
          const separator = entry.indexOf(':');
          return { buildId: entry.slice(0, separator), label: entry.slice(separator + 1) || null };
        });
        const data = await azureDevOpsService.getSkippedTestResultsForBuilds(targets);
        if (mounted) setResults(data);
      } catch (err) {
        console.error('Erreur récupération des tests ignorés:', err);
        if (mounted) setError('Impossible de récupérer les motifs des tests ignorés');
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetchSkipped();
    return () => { mounted = false; };
  }, [buildsKey, expectedTotal]);

  const reasons = useMemo(() => groupSkipReasons(results), [results]);
  const detailed = reasons.reduce((sum, r) => sum + r.count, 0);
  const missing = Math.max(0, (Number(expectedTotal) || 0) - detailed);
  const total = Math.max(Number(expectedTotal) || 0, detailed);

  if (!loading && !error && total === 0) return null;

  return (
    <div className="bg-white rounded-xl shadow-md overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between gap-3 px-5 py-4 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-2.5 min-w-0">
          <Clock className="w-5 h-5 text-gray-400 shrink-0" />
          <span className="font-semibold text-gray-900">{title}</span>
          <span className="text-sm font-bold text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full shrink-0">
            {total}
          </span>
          {!loading && !error && reasons.length > 0 && (
            <span className="text-xs text-gray-500 truncate">
              {reasons.length} motif{reasons.length > 1 ? 's' : ''}
            </span>
          )}
        </div>
        {open ? (
          <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" />
        ) : (
          <ChevronRight className="w-4 h-4 text-gray-400 shrink-0" />
        )}
      </button>

      {open && (
        <div className="px-5 pb-5 border-t border-gray-100 pt-4">
          {loading && (
            <div className="flex items-center gap-2 text-sm text-gray-500 py-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              Récupération des motifs…
            </div>
          )}

          {error && (
            <div className="flex items-start gap-2 text-sm text-orange-700 bg-orange-50 border border-orange-200 rounded-lg px-3 py-2">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {!loading && !error && (
            <div className="space-y-2">
              {reasons.map(reason => {
                const share = total ? (reason.count / total) * 100 : 0;
                const isExpanded = expandedKey === reason.key;
                return (
                  <div key={reason.key} className="rounded-lg border border-gray-100 overflow-hidden">
                    <button
                      type="button"
                      onClick={() => setExpandedKey(isExpanded ? null : reason.key)}
                      className="w-full text-left px-3 py-2.5 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <span
                          className={`text-sm leading-snug ${reason.isEmpty ? 'italic text-gray-400' : 'text-gray-700'}`}
                          title={reason.label}
                        >
                          {reason.label}
                        </span>
                        <span className="text-sm font-bold text-gray-900 shrink-0 tabular-nums">
                          {reason.count}
                        </span>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-1.5 mt-2">
                        <div
                          className={`h-1.5 rounded-full ${reason.isEmpty ? 'bg-gray-300' : 'bg-slate-400'}`}
                          style={{ width: `${Math.min(share, 100)}%` }}
                        />
                      </div>
                    </button>

                    {isExpanded && (
                      <ul className="bg-gray-50 border-t border-gray-100 px-3 py-2 space-y-1 max-h-56 overflow-y-auto">
                        {reason.tests.map((test, i) => (
                          <li key={`${test.testName}-${i}`} className="text-xs text-gray-600 flex gap-2">
                            <span className="text-gray-300 shrink-0">•</span>
                            <span className="min-w-0">
                              <span className="break-words">{test.testName}</span>
                              {(test.squadName || test.suiteName) && (
                                <span className="text-gray-400">
                                  {' — '}
                                  {test.squadName || test.suiteName}
                                </span>
                              )}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                );
              })}

              {missing > 0 && (
                <p className="text-xs text-gray-400 pt-1">
                  {missing} test{missing > 1 ? 's' : ''} ignoré{missing > 1 ? 's' : ''} sans détail
                  récupérable (résultat absent de l'API).
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
